// « Préparer la facture » : branchement réel de prepareFromExpenses (CIME, Polaris, stockage).
import { supabase } from '../../lib/supabaseClient';
import { listDossierContacts } from '../contacts/contactService';
import { createBillingDocument, replaceBillingLines } from './billingService';
import { downloadExpenseReceipt, type DossierExpense } from './expenseService';
import {
  createPolarDisbursement,
  createPolarSupplier,
  ensurePolarParty,
  findPolarSupplier,
  getPolarDisbursementLineIds,
  savePolarDisbursement,
  uploadPolarDisbursementReceipt,
} from './polarClient';
import { contactToPolarParty, createPolarClientDeps } from './polarBilling';
import { prepareFromExpenses, type PrepareResult } from '../../lib/billing/prepareFromExpenses';
import type { PreparationPlan } from '../../lib/billing/preparation';

export async function prepareInvoiceFromExpenses(
  plan: Extract<PreparationPlan<DossierExpense>, { ok: true }>,
  dossierLabel: string,
): Promise<PrepareResult> {
  const polar = createPolarClientDeps();
  return prepareFromExpenses(plan, {
    findClient: async (dossierId) => {
      const parties = await listDossierContacts(dossierId);
      const client = parties.find((p) => p.role === 'client' && p.contact);
      return client?.contact ? { contactId: client.contact_id, party: contactToPolarParty(client.contact) } : null;
    },
    createFraisInvoice: async (dossierId, clientContactId, lines) => {
      const doc = await createBillingDocument({
        dossier_id: dossierId,
        doc_type: 'invoice',
        client_contact_id: clientContactId,
        vat_rate: 20,
        notes: `Frais — ${dossierLabel}`,
      });
      await replaceBillingLines(doc.id, lines);
      return doc.id;
    },
    mandate: async (dossierId) => {
      const { data, error } = await supabase
        .from('dossiers')
        .select('debours_mandate_on, debours_mandate_ref')
        .eq('id', dossierId)
        .single();
      if (error) throw new Error(`Failed to read mandate: ${error.message}`);
      return { on: data.debours_mandate_on, ref: data.debours_mandate_ref };
    },
    polarContactId: async (party) => (await ensurePolarParty(party, polar)).contactId,
    findSupplier: (name) => findPolarSupplier(name, polar),
    createSupplier: (name) => createPolarSupplier(name, polar),
    createDisbursement: () => createPolarDisbursement(polar),
    saveDisbursement: (payload) => savePolarDisbursement(payload, polar),
    disbursementLineIds: (id) => getPolarDisbursementLineIds(id, polar),
    downloadReceipt: (path) => downloadExpenseReceipt(path),
    uploadReceipt: (id, lineId, file, name) => uploadPolarDisbursementReceipt(id, lineId, file, name, polar),
    markBilled: async (ids, link) => {
      const { error } = await supabase
        .from('dossier_expenses')
        .update({ billed_at: new Date().toISOString(), ...link })
        .in('id', ids);
      if (error) throw new Error(`Failed to mark expenses as billed: ${error.message}`);
    },
  });
}
